import React, { useEffect, useMemo, useState } from "react";

import "@fontsource/manrope";
import { Box, Grid, MenuItem } from "@mui/material";
import { createGenerateClassName, StylesProvider } from "@mui/styles";
import { colors } from "@zapalacorp/tokens";
import { AutoSuggest, Button, Input, Select } from "Components/Form";
import { Color } from "Components/Form/Button";
import Checkbox from "Components/Form/Checkbox";
import RadioButton from "Components/Form/RadioGroup";
import { TooltipPlacements } from "Components/Form/ToolTipInfo";
import { AccordionStep } from "Components/Generic/AccordionStep";
import { Breadcrumb } from "Components/Generic/Breadcrumb";
import Check from "Components/Generic/Icons/Check";
import {
  EquipmentIcons,
  getFleetIconEquipmentNames,
  getGeneralIconEquipmentNames,
} from "Components/Generic/Icons/Equipment";
import SkeletonComponent from "Components/Generic/Skeleton";
import { UploadImageFileInput } from "Components/Generic/UploadImage";
import { isBefore } from "date-fns";
import { useYupErrors } from "Hooks/useYupValidation";
import { Branch } from "Models/branch";
import { Fleet } from "Models/fleet";
import { Member } from "Models/members/member";
import { Picture } from "Models/picture";
import { Warehouse } from "Models/warehouse";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import IconWrapper from "../../../../../../../components/IconWrapper";
import {
  EquipmentOwnership,
  EquipmentType,
  FleetType,
  FleetTypeMode,
  Status,
} from "../../../../../../../util/enums";
import { formatCurrencyToInput } from "../../../../../../../util/formatCurrencyToInput";
import { getMeasurement } from "../../../../../../../util/getMeasurement";
import { currencyToNumber } from "../../../../../../../util/prices";
import { MembersStep } from "../../Subcontractors/SubcontractorsModal/components/MembersStep";
import useEquipmentActions from "../hooks/useEquipmentActions";
import { CustomRadioButton, SimpleText } from "./styles";
import {
  AccordionDetailsWrapper,
  BranchOption,
  BranchOptionText,
  CheckedBox,
  ChooseOwnershipBox,
  ChooseTypeBox,
  Container,
  FieldsContainer,
  Footer,
  IconBox,
  IconBoxContent,
  IconsContainer,
  IconsTitle,
  LeftContentContainer,
  OwnershipSubcontractorBox,
  StepsWrapper,
  StorageBoxLoadableDivider,
  useStyles,
} from "./styles2";
import { useAppState } from "Context/StateContext";

let temporaryBranchesSelected: Branch[] = [];

const generateClassName = createGenerateClassName({
  productionPrefix: "cfl-",
  seed: "fleet-create-page",
});

const Creatnew: React.FC = () => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const className = useStyles();

  const {
    getAllBranches,
    getFleetTypes,
    getAllWarehouses,
    getAllTeams,
    getAllSubcontractors,
    useCreateFleet,
    onBackToEquipmentsTab,
  } = useEquipmentActions();
  const { data: warehouseList, isLoading: loadingWarehouses } = getAllWarehouses();
  const { data: branchList, isLoading: loadingBranches } = getAllBranches();
  const { data: fleetTypes, isLoading: loadingFleetTypes } = getFleetTypes();
  const { data: teamList, isLoading: loadingTeams } = getAllTeams();
  const { data: subcontractorList, isLoading: loadingSubcontractors } = getAllSubcontractors();

  const { mutate: createFleet, isLoading: isLoadingCreate } = useCreateFleet(() => {
    onBackToEquipmentsTab();
  });

  const currency = currentUser?.company?.currency;

  const regexToPrice = useMemo(() => {
    return /^\d+(\.(\d{1,2})?)?$/;
  }, [currency]);

  const [fields, setFields] = useState<Partial<Fleet>>({
    status: Status.ACTIVE,
  });
  const [isFirstStepOpen, setIsFirstStepOpen] = useState<boolean>(true);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [membersAdded, setMembersAdded] = useState<Member[]>([]);

  const { getErrorByField, someFieldHasError } = useYupErrors<Fleet>(errors);

  const onSetAllField = (value: any) => {
    setFields((prev) => ({ ...prev, ...value }));
  };

  const onSetField = (key: keyof Fleet, value: any) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const onChangeField = (key: keyof Fleet, event: React.ChangeEvent<any>) => {
    if (event && event.target) {
      setFields((prev) => ({ ...prev, [key]: event.target.value }));
    }
  };

  useEffect(() => {
    if (fields.equipmentType === EquipmentType.FLEET) onSetField("iconName", "truck");
    if (fields.equipmentType === EquipmentType.GENERAL) onSetField("iconName", "tools");
  }, [fields.equipmentType]);

  const schema = yup.object().shape({
    // default
    equipmentType: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    branches: yup
      .array()
      .test("empty-check", translate("thisFieldIsRequired"), (fleetBranches) =>
        fleetBranches ? fleetBranches.length > 0 : false
      ),
    warehouseId: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    ownership: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    subcontractorId: yup.number().when("ownership", {
      is: (value: Fleet["ownership"]) => value?.toLowerCase() === "subcontractor",
      then: yup
        .number()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      otherwise: yup.number().nullable(),
    }),

    fleetTypeId: yup.number().when("equipmentType", {
      is: (value: Fleet["equipmentType"]) => value === EquipmentType.FLEET,
      then: yup
        .number()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
    typeMode: yup.string().when("fleetTypeName", {
      is: (value: string) => value?.toLowerCase() === FleetType.TRUCK.toLowerCase(),
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
    fleetNumber: yup.string().when("equipmentType", {
      is: (value: Fleet["equipmentType"]) => value === EquipmentType.FLEET,
      then: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    }),
    description: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    ratePerHour: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const onValidateFields = () => {
    const data = {
      ...fields,
      fleetTypeName:
        fleetTypes?.find(
          (type) =>
            fields.fleetTypeId &&
            type?.id === fields.fleetTypeId &&
            type?.name?.toLowerCase().includes(FleetType.TRUCK.toLowerCase())
        )?.name ?? "",
    };

    try {
      schema.validateSync(data, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };

  const ownershipOptions = useMemo(
    () => [
      { label: translate("own"), value: EquipmentOwnership.OWN },
      { label: translate("subcontractor"), value: EquipmentOwnership.SUBCONTRACTOR },
      { label: translate("rental"), value: EquipmentOwnership.RENTAL },
      { label: translate("leased"), value: EquipmentOwnership.LEASED },
    ],
    []
  );

  const fleetTypeEquipmentIconsNames = getFleetIconEquipmentNames();
  const generalTypeEquipmentIconsNames = getGeneralIconEquipmentNames();

  const fleetTypeModeList = useMemo(
    () => [
      { label: FleetTypeMode.PANTECH, value: FleetTypeMode.PANTECH },
      { label: FleetTypeMode.FLATBED, value: FleetTypeMode.FLATBED },
    ],
    []
  );

  const onChangePicture = (picture?: string) => {
    setFields((prev) => ({
      ...prev,
      picture: { mediaLink: picture } as Picture,
      pictureId: null,
    }));
  };

  const renderLeftContentByEquipmentTypeSelected = () => {
    return (
      <LeftContentContainer>
        <UploadImageFileInput
          labelText={translate("equipmentPhoto")}
          onChangePicture={(value) => {
            onChangePicture(value);
          }}
          picture={fields.picture?.mediaLink}
          onRemovePicture={() => {
            setFields((prev) => ({ ...prev, picture: undefined }));
          }}
        />
        <IconsContainer>
          <IconsTitle>{translate("equipmentIcon")}</IconsTitle>
          {fields.equipmentType === EquipmentType.FLEET && (
            <IconBoxContent>
              {fleetTypeEquipmentIconsNames.map((name) => {
                const selected = fields.iconName === name;

                return (
                  <IconBox
                    key={name}
                    selected={selected}
                    onClick={() => onSetField("iconName", name)}
                  >
                    {selected && (
                      <CheckedBox>
                        <Check color={colors.white} width={16} height={16} />
                      </CheckedBox>
                    )}
                    <EquipmentIcons
                      name={name}
                      color={colors.gray600}
                      size={32}
                      typeOfEquipment={EquipmentType.FLEET}
                    />
                  </IconBox>
                );
              })}
            </IconBoxContent>
          )}
          {fields.equipmentType === EquipmentType.GENERAL && (
            <IconBoxContent>
              {generalTypeEquipmentIconsNames.map((name) => {
                const selected = fields.iconName === name;

                return (
                  <IconBox
                    key={name}
                    selected={selected}
                    onClick={() => onSetField("iconName", name)}
                  >
                    {selected && (
                      <CheckedBox>
                        <Check color={colors.white} width={16} height={16} />
                      </CheckedBox>
                    )}
                    <EquipmentIcons
                      name={name}
                      color={colors.gray600}
                      size={32}
                      typeOfEquipment={EquipmentType.GENERAL}
                    />
                  </IconBox>
                );
              })}
            </IconBoxContent>
          )}
        </IconsContainer>
      </LeftContentContainer>
    );
  };

  const renderDetailsContentByEquipmentTypeSelected = () => {
    if (fields?.equipmentType === EquipmentType.FLEET) {
      const isTruckSelected = fleetTypes?.find(
        (type) => type?.id === fields?.fleetTypeId && type?.name?.toLowerCase().includes("truck")
      );

      return (
        <Grid item xs={9} container direction="row" spacing={3}>
          <Grid item xs={4}>
            <SkeletonComponent isLoading={loadingFleetTypes}>
              <Select
                label={translate("type")}
                value={fields.fleetTypeId}
                onChange={(event: any) => onChangeField("fleetTypeId", event)}
                required
                error={!!getErrorByField("fleetTypeId")}
                errorMessage={getErrorByField("fleetTypeId")}
                placeholder={translate("select")}
              >
                {(fleetTypes || []).map((type, index) => (
                  <MenuItem value={type.id} key={index}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </SkeletonComponent>
          </Grid>
          {isTruckSelected && (
            <Grid item xs={4}>
              <Select
                data-cy="fleetModeType"
                name="fleetModeType"
                label={translate("typeMode")}
                required
                value={fields?.typeMode}
                onChange={(event: any) => onChangeField("typeMode", event)}
                error={!!getErrorByField("typeMode")}
                errorMessage={getErrorByField("typeMode")}
              >
                {fleetTypeModeList.map((modeType) => (
                  <MenuItem data-cy={modeType.value} value={modeType.value} key={modeType.value}>
                    {modeType.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}
          <Grid item xs={4}>
            <Select
              label={translate("status")}
              value={fields.status}
              onChange={(event: any) => onChangeField("status", event)}
              placeholder={translate("select")}
              required
              error={!!getErrorByField("status")}
              errorMessage={getErrorByField("status")}
            >
              {[Status.ACTIVE, Status.INACTIVE].map((option, index) => (
                <MenuItem value={option} key={index}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {!isTruckSelected && <Grid item xs={4}></Grid>}
          <Grid item xs={4}>
            <Input
              label={translate("description")}
              value={fields.description}
              onChange={(event) => onChangeField("description", event)}
              required
              error={!!getErrorByField("description")}
              errorMessage={getErrorByField("description")}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("nickname")}
              value={fields.nickname}
              onChange={(event) => onChangeField("nickname", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("fleetNumber")}
              value={fields.fleetNumber}
              onChange={(event) => onChangeField("fleetNumber", event)}
              required
              error={!!getErrorByField("fleetNumber")}
              errorMessage={getErrorByField("fleetNumber")}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("vinNumber")}
              value={fields.vinNumber}
              onChange={(event) => onChangeField("vinNumber", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("make")}
              value={fields.make}
              onChange={(event) => onChangeField("make", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("model")}
              value={fields.model}
              onChange={(event) => onChangeField("model", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("year")}
              value={fields.year}
              onChange={(event) => onChangeField("year", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("engineType")}
              value={fields.engine}
              onChange={(event) => onChangeField("engine", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("plateNumber")}
              value={fields.licensePlate}
              onChange={(event) => onChangeField("licensePlate", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("fuelType")}
              value={fields.fuelType}
              onChange={(event) => onChangeField("fuelType", event)}
              placeholder={translate("select")}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("condition")}
              value={fields.condition}
              onChange={(event) => onChangeField("condition", event)}
              placeholder={translate("select")}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("capacity")}
              value={fields.capacityM3}
              onChange={(event) => onChangeField("capacityM3", event)}
              rightIcon={<span>{getMeasurement()?.code}</span>}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("euronorm")}
              value={fields.euronorm}
              onChange={(event) => onChangeField("euronorm", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("driverLicense")}
              value={fields.driverLicense}
              onChange={(event) => onChangeField("driverLicense", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("manufacturer")}
              value={fields.manufacturer}
              onChange={(event) => onChangeField("manufacturer", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <AutoSuggest
              disableCloseOnSelect
              label={translate("branch")}
              placeholder={fields?.branches?.length ? "" : translate("select")}
              ignoreValue
              required
              onOpen={() => {
                temporaryBranchesSelected = fields?.branches || [];
              }}
              onChange={(branches: Branch[]) => {
                temporaryBranchesSelected = branches;
                onSetField("branches", temporaryBranchesSelected);
              }}
              onClose={() => {
                onSetField("branches", temporaryBranchesSelected);
                temporaryBranchesSelected = [];
              }}
              defaultValue={fields?.branches || []}
              error={!!getErrorByField("branches")}
              errorMessage={getErrorByField("branches")}
              multiple
              options={branchList || []}
              getOptionLabel={(branch?: Branch) => (branch && branch.name ? branch.name : "")}
              getOptionSelected={(option: Branch, value: Branch) => {
                return option.id === value.id;
              }}
              renderOption={(
                props: React.HTMLAttributes<any>,
                branch: Branch,
                { selected }: { selected: boolean }
              ) => (
                <BranchOption {...props}>
                  <Checkbox checked={selected} />
                  <BranchOptionText>{branch.name}</BranchOptionText>
                </BranchOption>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <SkeletonComponent isLoading={loadingWarehouses}>
              <AutoSuggest
                dataCy="warehouseAutosuggest"
                label={translate("warehouse")}
                required
                placeholder={translate("searchForWarehouse")}
                error={!!getErrorByField("warehouseId")}
                errorMessage={getErrorByField("warehouseId")}
                inputValue={fields?.warehouse?.name || ""}
                getOptionLabel={(warehouse?: Warehouse) =>
                  warehouse && warehouse.name ? warehouse.name : ""
                }
                getOptionSelected={(option?: Warehouse, value?: Warehouse) =>
                  option?.id === value?.id
                }
                options={warehouseList || []}
                onChange={(warehouse: Warehouse) => {
                  onSetAllField({ warehouse, warehouseId: warehouse?.id });
                }}
              />
            </SkeletonComponent>
          </Grid>
          <Grid item xs={4}>
            <AutoSuggest
              disableCloseOnSelect
              label={translate("team")}
              placeholder={fields?.teams?.length ? "" : translate("select")}
              ignoreValue
              onChange={(teams: Fleet["teams"]) => {
                onSetField("teams", teams);
              }}
              defaultValue={fields?.teams || []}
              multiple
              options={teamList || []}
              getOptionLabel={(team?: any) => (team && team.name ? team.name : "")}
              getOptionSelected={(option: any, value: any) => {
                return option.id === value.id;
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("rate")}
              value={fields?.ratePerHour}
              onChange={(event) => {
                const value = event.target.value;
                if (!value || (value && regexToPrice.test(value))) {
                  onSetField("ratePerHour", event.target.value);
                }
              }}
              onBlur={(event) => {
                onSetField(
                  "ratePerHour",
                  formatCurrencyToInput(currencyToNumber(event.target.value))
                );
              }}
              required
              placeholder={"0.00"}
              leftIcon={<IconWrapper>{currency?.symbol || "$"}</IconWrapper>}
              rightIcon={<IconWrapper>/{translate("hour")}</IconWrapper>}
              inputTextAlign={"start"}
              error={!!getErrorByField("ratePerHour")}
              errorMessage={getErrorByField("ratePerHour")}
            />
          </Grid>
        </Grid>
      );
    }
    if (fields?.equipmentType === EquipmentType.GENERAL) {
      return (
        <Grid item xs={9} container direction="row" spacing={3}>
          <Grid item xs={4}>
            <Input
              label={translate("description")}
              value={fields.description}
              onChange={(event) => onChangeField("description", event)}
              required
              error={!!getErrorByField("description")}
              errorMessage={getErrorByField("description")}
              placeholder={translate("writeADescription")}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("make")}
              value={fields.make}
              onChange={(event) => onChangeField("make", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Select
              label={translate("status")}
              value={fields.status}
              onChange={(event: any) => onChangeField("status", event)}
              placeholder={translate("select")}
              required
              error={!!getErrorByField("status")}
              errorMessage={getErrorByField("status")}
            >
              {[Status.ACTIVE, Status.INACTIVE].map((option, index) => (
                <MenuItem value={option} key={index}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("model")}
              value={fields.model}
              onChange={(event) => onChangeField("model", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("year")}
              value={fields.year}
              onChange={(event) => onChangeField("year", event)}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("condition")}
              value={fields.condition}
              onChange={(event) => onChangeField("condition", event)}
              placeholder={translate("select")}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("capacity")}
              value={fields.capacityM3}
              onChange={(event) => onChangeField("capacityM3", event)}
              rightIcon={<span>{getMeasurement()?.code}</span>}
            />
          </Grid>
          <Grid item xs={4}>
            <SkeletonComponent isLoading={loadingBranches}>
              <AutoSuggest
                disableCloseOnSelect
                required
                label={translate("branch")}
                placeholder={fields?.branches?.length ? "" : translate("select")}
                ignoreValue
                onOpen={() => {
                  temporaryBranchesSelected = fields?.branches || [];
                }}
                onChange={(branches: Branch[]) => {
                  temporaryBranchesSelected = branches;
                  onSetField("branches", temporaryBranchesSelected);
                }}
                onClose={() => {
                  onSetField("branches", temporaryBranchesSelected);
                  temporaryBranchesSelected = [];
                }}
                defaultValue={fields?.branches || []}
                error={!!getErrorByField("branches")}
                errorMessage={getErrorByField("branches")}
                multiple
                options={branchList || []}
                getOptionLabel={(branch?: Branch) => (branch && branch.name ? branch.name : "")}
                getOptionSelected={(option: Branch, value: Branch) => {
                  return option.id === value.id;
                }}
                renderOption={(
                  props: React.HTMLAttributes<any>,
                  branch: Branch,
                  { selected }: { selected: boolean }
                ) => (
                  <BranchOption {...props}>
                    <Checkbox checked={selected} />
                    <BranchOptionText>{branch.name}</BranchOptionText>
                  </BranchOption>
                )}
              />
            </SkeletonComponent>
          </Grid>
          <Grid item xs={4}>
            <SkeletonComponent isLoading={loadingWarehouses}>
              <AutoSuggest
                dataCy="warehouseAutosuggest"
                label={translate("warehouse")}
                required
                placeholder={translate("select")}
                error={!!getErrorByField("warehouseId")}
                errorMessage={getErrorByField("warehouseId")}
                inputValue={fields?.warehouse?.name || ""}
                getOptionLabel={(warehouse?: Warehouse) =>
                  warehouse && warehouse.name ? warehouse.name : ""
                }
                getOptionSelected={(option?: Warehouse, value?: Warehouse) =>
                  option?.id === value?.id
                }
                options={warehouseList || []}
                onChange={(warehouse: Warehouse) => {
                  onSetAllField({ warehouse, warehouseId: warehouse?.id });
                }}
              />
            </SkeletonComponent>
          </Grid>
          <Grid item xs={4}>
            <SkeletonComponent isLoading={loadingTeams}>
              <AutoSuggest
                disableCloseOnSelect
                label={translate("team")}
                placeholder={fields?.teams?.length ? "" : translate("select")}
                ignoreValue
                onChange={(teams: Fleet["teams"]) => {
                  onSetField("teams", teams);
                }}
                defaultValue={fields?.teams || []}
                multiple
                options={teamList || []}
                getOptionLabel={(team?: any) => (team && team.name ? team.name : "")}
                getOptionSelected={(option: any, value: any) => {
                  return option.id === value.id;
                }}
              />
            </SkeletonComponent>
          </Grid>
          <Grid item xs={4}>
            <Input
              label={translate("rate")}
              value={fields?.ratePerHour}
              onChange={(event) => {
                const value = event.target.value;
                if (!value || (value && regexToPrice.test(value))) {
                  onSetField("ratePerHour", event.target.value);
                }
              }}
              onBlur={(event) => {
                onSetField(
                  "ratePerHour",
                  formatCurrencyToInput(currencyToNumber(event.target.value))
                );
              }}
              required
              placeholder={"0.00"}
              leftIcon={<IconWrapper>{currency?.symbol || "$"}</IconWrapper>}
              rightIcon={<IconWrapper>/{translate("hour")}</IconWrapper>}
              inputTextAlign={"start"}
              error={!!getErrorByField("ratePerHour")}
              errorMessage={getErrorByField("ratePerHour")}
            />
          </Grid>
        </Grid>
      );
    }
  };

  const renderFleetLoadable = () => {
    if (fields?.equipmentType === EquipmentType.FLEET) {
      return (
        <React.Fragment>
          <StorageBoxLoadableDivider />
          <Grid container direction="row">
            <Grid item xs={12}>
              <Checkbox
                control={{
                  "data-cy": "isLoadable",
                }}
                checked={fields.isLoadable}
                label={translate("enableThisFleetAsAStorageBox")}
                onChange={(_, checked) => onSetField("isLoadable", checked)}
                tooltipText={translate(
                  "enablingThisAllowsTheUserToChooseTheVehicleForStorageMovementOrTransshipmentAndIndicateWhenItemsArePlacedInsideLikeAStorageBox"
                )}
                tooltipPlacement={TooltipPlacements.TOP_END}
              />
            </Grid>
          </Grid>
        </React.Fragment>
      );
    }

    return null;
  };

  const getTypeEquipmentStep = (stepNumber: number) => {
    return (
      <AccordionStep.Root
        hasError={someFieldHasError(["equipmentType"])}
        expanded={isFirstStepOpen}
        onChange={() => setIsFirstStepOpen(!isFirstStepOpen)}
      >
        <AccordionStep.Header
          title={translate("chooseTheTypeOfEquipment")}
          step={stepNumber}
          subtitle=""
          isMandatory
          hasError={someFieldHasError(["equipmentType"])}
        />

        <AccordionStep.Divider />

        <AccordionDetailsWrapper>
          <Grid container direction="row" justifyContent="space-between">
            <ChooseTypeBox
              item
              selected={fields.equipmentType === EquipmentType.FLEET}
              onClick={() => onSetField("equipmentType", EquipmentType.FLEET)}
              optionQuantity={2}
              optionSpacing="8px"
            >
              <CustomRadioButton
                value={fields?.equipmentType}
                options={[{ label: translate("fleet"), value: EquipmentType.FLEET }]}
                onChange={(event) => onChangeField("equipmentType", event)}
              />
              <SimpleText>
                {translate("chooseAVehicleTypeOfEquipmentLikeAForkliftOrATruck")}
              </SimpleText>
            </ChooseTypeBox>
            <ChooseTypeBox
              item
              selected={fields.equipmentType === EquipmentType.GENERAL}
              onClick={() => onSetField("equipmentType", EquipmentType.GENERAL)}
              optionQuantity={2}
              optionSpacing="8px"
            >
              <CustomRadioButton
                value={fields?.equipmentType}
                options={[{ label: translate("general"), value: EquipmentType.GENERAL }]}
                onChange={(event) => onChangeField("equipmentType", event)}
              />
              <SimpleText>{translate("createMoreGeneralizedEquipmentWithAFewDetails")}</SimpleText>
            </ChooseTypeBox>
          </Grid>
        </AccordionDetailsWrapper>
      </AccordionStep.Root>
    );
  };

  const getEquipmentDetailsStep = (stepNumber: number) => {
    return (
      <AccordionStep.Root
        hasError={someFieldHasError([
          "fleetTypeId",
          "typeMode",
          "branches",
          "warehouse",
          "licensePlace",
          "fleetNumber",
          "description",
        ])}
      >
        <AccordionStep.Header
          title={translate("equipmentDetails")}
          step={stepNumber}
          subtitle=""
          isMandatory
          hasError={someFieldHasError([
            "fleetTypeId",
            "typeMode",
            "branches",
            "warehouse",
            "licensePlace",
            "fleetNumber",
            "description",
          ])}
        />
        <AccordionStep.Divider />
        <AccordionDetailsWrapper>
          <FieldsContainer>
            {renderLeftContentByEquipmentTypeSelected()}
            {renderDetailsContentByEquipmentTypeSelected()}
          </FieldsContainer>
          {renderFleetLoadable()}
        </AccordionDetailsWrapper>
      </AccordionStep.Root>
    );
  };

  const getOwnershipStep = (stepNumber: number) => {
    return (
      <AccordionStep.Root hasError={someFieldHasError(["ownership", "subcontractorId"])}>
        <AccordionStep.Header
          title={translate("ownership")}
          step={stepNumber}
          subtitle={translate("whoOwnsThisEquipment")}
          isMandatory
          hasError={someFieldHasError(["ownership", "subcontractorId"])}
        />
        <AccordionStep.Divider />

        <AccordionDetailsWrapper>
          <Grid container direction="row" justifyContent="space-between">
            {ownershipOptions.map(({ value, label }) => (
              <ChooseOwnershipBox
                key={value}
                item
                selected={fields.ownership === value}
                onClick={() => {
                  if (value !== EquipmentOwnership.SUBCONTRACTOR) {
                    onSetField("ownership", value);
                    onSetField("subcontractorId", null);
                    onSetField("subcontractor", null);
                  }
                  onSetField("ownership", value);
                }}
                optionQuantity={ownershipOptions.length}
                optionSpacing="16px"
              >
                <RadioButton
                  value={fields?.ownership}
                  options={[{ label, value, classes: className.radioButton }]}
                  onChange={() => onSetField("ownership", value)}
                />
              </ChooseOwnershipBox>
            ))}
          </Grid>
          {fields.ownership === EquipmentOwnership.SUBCONTRACTOR && (
            <Grid container direction="row">
              <OwnershipSubcontractorBox item xs={5}>
                <SkeletonComponent isLoading={loadingSubcontractors}>
                  <Select
                    label={translate("subcontractor")}
                    value={fields.subcontractorId}
                    onChange={(event: any) => onChangeField("subcontractorId", event)}
                    required
                    error={!!getErrorByField("subcontractorId")}
                    errorMessage={getErrorByField("subcontractorId")}
                  >
                    {(subcontractorList || []).map((subcontractor, index) => (
                      <MenuItem value={subcontractor?.id} key={index}>
                        {subcontractor?.businessName}
                      </MenuItem>
                    ))}
                  </Select>
                </SkeletonComponent>
              </OwnershipSubcontractorBox>
            </Grid>
          )}
        </AccordionDetailsWrapper>
      </AccordionStep.Root>
    );
  };

  useEffect(() => {
    if (!isBefore(new Date(fields?.purchaseDate!), new Date(fields?.soldDate!)))
      setFields((prev) => ({ ...prev, soldDate: undefined }));
  }, [fields?.purchaseDate, fields?.soldDate]);

  const getDatesStep = (stepNumbers: number) => {
    return (
      <AccordionStep.Root>
        <AccordionStep.Header
          title={translate("dates")}
          step={stepNumbers}
          subtitle=""
          isMandatory={false}
        />
        <AccordionStep.Divider />
        <AccordionDetailsWrapper>
          <Grid container direction="row" spacing={3} style={{ marginTop: "12px" }}>
            <Grid item xs={3}>
              <Input
                label={translate("purchaseDate")}
                value={fields?.purchaseDate}
                onChange={(event) => onChangeField("purchaseDate", event)}
                type="date"
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                label={translate("soldDate")}
                value={fields?.soldDate}
                onChange={(event) => onChangeField("soldDate", event)}
                type="date"
              />
            </Grid>
          </Grid>
        </AccordionDetailsWrapper>
      </AccordionStep.Root>
    );
  };

  const getMembersStep = (stepNumbers: number) => {
    return (
      <AccordionStep.Root>
        <AccordionStep.Header
          title={translate("members")}
          step={stepNumbers}
          subtitle=""
          isMandatory={false}
        />
        <AccordionDetailsWrapper>
          <AccordionStep.Divider />
          <MembersStep membersAdded={membersAdded} setMembersAdded={setMembersAdded} />
        </AccordionDetailsWrapper>
      </AccordionStep.Root>
    );
  };

  const getSteps = () => {
    const stepsToSend = [
      {
        getRender: (step: number) => getTypeEquipmentStep(step),
      },
    ];

    if (fields.equipmentType) {
      stepsToSend.push({
        getRender: (step: number) => getEquipmentDetailsStep(step),
      });
      stepsToSend.push({
        getRender: (step: number) => getOwnershipStep(step),
      });
      if (fields?.equipmentType === "Fleet") {
        stepsToSend.push({
          getRender: (step: number) => getDatesStep(step),
        });
      }
      stepsToSend.push({
        getRender: (step: number) => getMembersStep(step),
      });
    }

    return stepsToSend;
  };
  const steps = getSteps();

  const submit = () => {
    if (onValidateFields()) {
      createFleet({
        fleet: {
          ...fields,
          members: membersAdded || [],
          ratePerHour: fields?.ratePerHour
            ? currencyToNumber(String(fields?.ratePerHour))
            : undefined,
        } as Fleet,
        addToRoutePlanner: false,
      });
    }
  };

  return (
    <StylesProvider generateClassName={generateClassName}>
      <Breadcrumb
        items={[
          {
            key: "settings",
            label: translate("settings"),
            onClick: onBackToEquipmentsTab,
          },
          {
            key: "equipments",
            label: translate("equipments"),
            onClick: onBackToEquipmentsTab,
          },
          {
            key: "create",
            label: translate("createNew"),
          },
        ]}
      />
      <Container>
        <StepsWrapper>
          {steps.map(({ getRender }, index) => (
            <React.Fragment key={index}>{getRender(index + 1)}</React.Fragment>
          ))}
        </StepsWrapper>

        <Footer>
          <Box>
            <Button customColor={Color.CANCEL} onClick={onBackToEquipmentsTab}>
              {translate("cancel")}
            </Button>
          </Box>
          <Box>
            <Button
              customColor={Color.SUCCESS}
              onClick={() => submit()}
              loading={isLoadingCreate}
              disabled={isLoadingCreate || !!fields?.fleetType}
            >
              {translate("create")}
            </Button>
          </Box>
        </Footer>
      </Container>
    </StylesProvider>
  );
};

export default Creatnew;
